import React, { useEffect } from 'react';
import { Check, BookOpen, Brain, Battery, ShieldCheck } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { TestimonialCard } from '../components/TestimonialCard';
import { PurchasePopup } from '../components/PurchasePopup';
export function LandingPage() {
  // Set page title
  useEffect(() => {
    document.title = 'NO ESTÁS CANSADA, ESTÁS MENTALMENTE AGOTADA.';
  }, []);
  const testimonials = [{
    name: 'María',
    age: 34,
    city: 'Buenos Aires',
    content: "Sentía que mi cabeza no paraba nunca. Este libro me hizo entender que no era falta de energía, sino exceso de decisiones. La 'Rutina Mental de 5 Minutos' cambió mis noches.",
    imageInitial: 'M'
  }, {
    name: 'Laura',
    age: 29,
    city: 'México DF',
    content: 'Lloré leyendo la introducción. Fue como si alguien describiera exactamente lo que siento todos los días. La culpa desapareció cuando entendí el concepto de fatiga de decisión.',
    imageInitial: 'L'
  }, {
    name: 'Carolina',
    age: 41,
    city: 'Santiago',
    content: "Soy madre y trabajo full time. Pensé que necesitaba vacaciones, pero necesitaba este sistema. El checklist de 'Decidir lo Mínimo' es mi salvavidas diario.",
    imageInitial: 'C'
  }, {
    name: 'Valentina',
    age: 32,
    city: 'Bogotá',
    content: 'He leído mil libros de productividad y todos me exigían más. Este es el primero que me pide menos. Me siento más liviana y con más claridad mental.',
    imageInitial: 'V'
  }, {
    name: 'Sofía',
    age: 38,
    city: 'Montevideo',
    content: 'Increíble cómo algo tan simple puede ser tan potente. Dejé de sentirme saturada en una semana aplicando solo dos de los conceptos del libro.',
    imageInitial: 'S'
  }];
  return <div className="min-h-screen bg-[#FDFBF7] font-sans text-gray-800 selection:bg-purple-200" data-id="element-27">
      <PurchasePopup data-id="element-28" />

      {/* Hero Section */}
      <header className="bg-gradient-to-b from-[#EFE6F7] to-[#FDFBF7] pt-12 pb-16 px-4" data-id="element-29">
        <div className="max-w-4xl mx-auto text-center" data-id="element-30">
          <div className="inline-block bg-red-100 text-red-700 px-4 py-1 rounded-full text-sm font-bold mb-6 animate-pulse" data-id="element-31">
            ❌ NO ESTÁS CANSADA
          </div>

          <h1 className="text-4xl md:text-6xl font-extrabold text-[#4A3B69] mb-6 leading-tight" data-id="element-32">
            🧠 ESTÁS MENTALMENTE AGOTADA.
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-33">
            Cómo decidir menos, recuperar energía y dejar de sentirte saturada
            todos los días
          </p>

          <div className="mb-10 transform hover:scale-105 transition-transform duration-500 cursor-pointer" data-id="element-34">
            <a href="https://checkout.tupuntodigital.online/checkouts/cn/hWN7KxpeGG27ZWP6JSh1M5Yo/es-ar?_r=AQABJ92B-oXw3u4aJaiQFH9rJBZPDCuWjsbIZv68OKhEceU&preview_theme_id=147264340118" data-id="element-35">
              <img src="https://cdn.magicpatterns.com/uploads/7mvTUSBUVnrPfCgpeW4tQJ/Cover_portada.png" alt="No estás cansada, estás mentalmente agotada - Libro Digital" className="w-full max-w-md mx-auto drop-shadow-2xl rounded-lg" data-id="element-36" />
            </a>
          </div>

          <div className="text-gray-500 text-sm mb-8 italic" data-id="element-37">
            (aunque duermas, aunque no "hagas nada", aunque empieces a creer que
            el problema sos vos)
          </div>

          <CTAButton className="animate-bounce-subtle" data-id="element-38">
            QUIERO RECUPERAR MI ENERGÍA
          </CTAButton>
        </div>
      </header>

      {/* Sales Letter Content */}
      <main className="max-w-3xl mx-auto px-6 py-12" data-id="element-39">
        {/* The Problem */}
        <section className="prose prose-lg prose-purple mx-auto mb-16" data-id="element-40">
          <p className="font-medium text-xl text-[#4A3B69]" data-id="element-41">
            Si todo te cansa… incluso cuando no hiciste nada…
          </p>
          <p className="font-bold text-xl" data-id="element-42">leé esto con atención.</p>
          <p data-id="element-43">Porque lo que te está pasando no es normal, pero sí es común.</p>
          <p data-id="element-44">
            Dormís. Descansás "un poco". A veces incluso tenés tiempo libre.
          </p>
          <p className="font-semibold" data-id="element-45">Y aun así…</p>
          <p data-id="element-46">
            te sentís agotada. sin energía mental. sin ganas de pensar una cosa
            más.
          </p>
          <p data-id="element-47">
            Las decisiones más simples pesan. Elegir qué hacer, qué comer, qué
            priorizar… abruma.
          </p>
          <p data-id="element-48">Y aparece esa sensación silenciosa que nadie ve:</p>
          <h3 className="text-2xl font-bold text-red-500 mt-8 mb-4" data-id="element-49">culpa.</h3>
          <p data-id="element-50">
            Culpa por sentirte así. Culpa por no poder más. Culpa por pensar que
            antes podías y ahora no.
          </p>

          <div className="bg-purple-50 border-l-4 border-purple-500 p-6 my-8 rounded-r-lg" data-id="element-51">
            <p className="font-bold text-lg text-purple-900 m-0" data-id="element-52">
              Pero dejame decirte algo importante, ahora mismo:
            </p>
            <p className="text-xl mt-2 mb-0" data-id="element-53">
              No estás cansada de hacer cosas. Estás cansada de decidir todo.
            </p>
          </div>

          <p data-id="element-54">Y cuando entendés eso, todo cambia.</p>
        </section>

        {/* The Explanation */}
        <section className="mb-16" data-id="element-55">
          <h2 className="text-3xl font-bold text-[#4A3B69] mb-6 text-center" data-id="element-56">
            El cansancio que nadie supo explicarte
          </h2>
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-57">
            <p className="text-lg mb-4" data-id="element-58">
              El problema no es tu energía. No es tu motivación. No es tu fuerza
              de voluntad.
            </p>
            <p className="text-xl font-bold text-purple-700 mb-6" data-id="element-59">
              Es sobrecarga mental.
            </p>
            <p className="mb-4" data-id="element-60">Decidir todo el tiempo:</p>
            <ul className="space-y-3 mb-6" data-id="element-61">
              {['qué hacer', 'qué dejar para después', 'qué es prioridad', 'qué comer', 'qué responder', 'qué pensar', 'qué postergar'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-62">
                  <div className="w-2 h-2 bg-purple-400 rounded-full" data-id="element-63"></div>
                  <span className="text-gray-700" data-id="element-64">{item}</span>
                </li>)}
            </ul>
            <p className="font-semibold text-lg" data-id="element-65">
              Todo el día. Todos los días.
            </p>
            <p className="mt-4 text-gray-600" data-id="element-66">
              Aunque no lo notes. Aunque parezcan decisiones pequeñas. Cada
              decisión consume energía mental. Y esa energía no es infinita.
            </p>
            <p className="mt-4 font-medium text-purple-800" data-id="element-67">
              Por eso estás cansada incluso cuando "no hiciste nada".
            </p>
          </div>
        </section>

        {/* Symptoms */}
        <section className="mb-16" data-id="element-68">
          <h2 className="text-3xl font-bold text-[#4A3B69] mb-8 text-center" data-id="element-69">
            Lo que pasa cuando vivís así (y lo sabés)
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mb-8" data-id="element-70">
            {['Te cuesta arrancar cosas simples', 'Postergás sin saber por qué', 'Te irritás más fácil', 'El descanso no te recarga', 'El tiempo libre no se disfruta', 'Todo pesa un poco más de lo que debería'].map((item, i) => <div key={i} className="flex items-start gap-3 bg-red-50 p-4 rounded-lg" data-id="element-71">
                <ShieldCheck className="w-5 h-5 text-red-400 mt-1 flex-shrink-0" data-id="element-72" />
                <span className="text-gray-800" data-id="element-73">{item}</span>
              </div>)}
          </div>

          <div className="text-center max-w-xl mx-auto" data-id="element-74">
            <p className="text-lg mb-2" data-id="element-75">Y aparece un pensamiento silencioso:</p>
            <p className="text-xl font-serif italic text-gray-500 mb-4" data-id="element-76">
              "¿Qué me pasa a mí?"
            </p>
            <p className="text-gray-700" data-id="element-77">
              Ese pensamiento es el más peligroso. Porque te hace creer que el
              problema sos vos.
            </p>
            <p className="text-2xl font-bold text-[#4A3B69] mt-4" data-id="element-78">No lo sos.</p>
          </div>
        </section>

        {/* Desires */}
        <section className="bg-[#F5F0FA] -mx-6 px-6 py-12 mb-16 rounded-3xl" data-id="element-79">
          <h2 className="text-3xl font-bold text-[#4A3B69] mb-6 text-center" data-id="element-80">
            Lo que en realidad estás buscando
          </h2>
          <p className="text-center mb-8 text-gray-600" data-id="element-81">
            No querés hacer más cosas. No querés ser más productiva. No querés
            organizarte mejor.
          </p>

          <div className="space-y-4 max-w-lg mx-auto" data-id="element-82">
            {['sentir la cabeza más liviana', 'decidir sin agotarte', 'tener claridad mental', 'dejar de vivir reaccionando', 'recuperar energía sin exigirte más'].map((item, i) => <div key={i} className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm" data-id="element-83">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-84" />
                <span className="text-lg font-medium text-gray-800" data-id="element-85">
                  {item}
                </span>
              </div>)}
          </div>

          <p className="text-center text-xl font-bold text-purple-800 mt-8" data-id="element-86">
            Querés calma mental funcional, no vacaciones.
          </p>
        </section>

        {/* Why other things failed */}
        <section className="mb-16 prose prose-lg prose-purple mx-auto" data-id="element-87">
          <h2 className="text-[#4A3B69]" data-id="element-88">
            Por qué nada de lo que probaste funcionó
          </h2>
          <p data-id="element-89">Probaste:</p>
          <ul className="grid grid-cols-2 gap-2 list-none pl-0" data-id="element-90">
            {['organizarte mejor', 'apps', 'listas', 'rutinas', 'motivarte', '“ponerle ganas”'].map((item, i) => <li key={i} className="bg-gray-100 px-3 py-2 rounded text-center text-gray-600 text-sm m-0" data-id="element-91">
                {item}
              </li>)}
          </ul>
          <p data-id="element-92">Funcionó… un rato. Después volviste al mismo cansancio.</p>
          <p className="font-bold" data-id="element-93">¿Por qué?</p>
          <p data-id="element-94">
            Porque todas esas soluciones atacan lo que hacés, pero ignoran todo
            lo que decidís antes.
          </p>
          <p data-id="element-95">
            La fuerza de voluntad se agota. La motivación se gasta. La
            organización suma carga si no reduce decisiones.
          </p>
        </section>

        {/* The Solution */}
        <section className="mb-16 text-center" data-id="element-96">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-97">
            La pieza que faltaba
          </h2>
          <p className="text-xl mb-8" data-id="element-98">
            El problema no es hacer más. Es decidir menos.
          </p>
          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white p-8 rounded-2xl shadow-xl transform rotate-1 hover:rotate-0 transition-transform duration-300" data-id="element-99">
            <p className="mb-4 opacity-90" data-id="element-100">A esto lo llamo:</p>
            <h3 className="text-3xl md:text-4xl font-extrabold mb-6 tracking-tight" data-id="element-101">
              🧩 EL SISTEMA ANTI-DECISIONES
            </h3>
            <p className="text-lg opacity-90 mb-0" data-id="element-102">
              Y es exactamente lo que vas a aprender acá.
            </p>
          </div>
        </section>

        {/* What is the ebook */}
        <section className="mb-16" data-id="element-103">
          <div className="flex flex-col md:flex-row items-center gap-8 mb-10" data-id="element-104">
            <div className="w-full md:w-1/2" data-id="element-105">
              <img src="https://cdn.magicpatterns.com/uploads/7mvTUSBUVnrPfCgpeW4tQJ/Cover_portada.png" alt="Ebook Cover" className="w-full rounded-lg shadow-lg rotate-[-2deg]" data-id="element-106" />
            </div>
            <div className="w-full md:w-1/2" data-id="element-107">
              <h2 className="text-3xl font-bold text-[#4A3B69] mb-4" data-id="element-108">
                Qué es este ebook
              </h2>
              <p className="text-xl font-semibold text-purple-600 mb-4" data-id="element-109">
                No estás cansada. Estás mentalmente agotada.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-110">
                Es una guía práctica y humana que te enseña:
              </p>
              <ul className="space-y-2 mb-6" data-id="element-111">
                {['por qué tu cansancio no es un defecto', 'cómo identificar las decisiones que te drenan', 'cómo reducir el ruido mental', 'cómo crear sistemas simples que te alivian', 'cómo sostener la calma sin volver al caos'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-112">
                    <BookOpen className="w-5 h-5 text-purple-500 mt-1 flex-shrink-0" data-id="element-113" />
                    <span className="text-gray-700 text-sm" data-id="element-114">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-xl text-center" data-id="element-115">
            <p className="font-medium text-yellow-800 m-0" data-id="element-116">
              No es motivación. No es autoayuda vacía. No es productividad
              extrema.
              <br data-id="element-117" />
              <span className="font-bold text-lg block mt-2" data-id="element-118">
                Es alivio mental aplicado.
              </span>
            </p>
          </div>
        </section>

        {/* Origin Story */}
        <section className="prose prose-lg prose-purple mx-auto mb-16" data-id="element-119">
          <h3 className="text-[#4A3B69]" data-id="element-120">Cómo nació este ebook</h3>
          <p data-id="element-121">Este ebook nació de una pregunta simple:</p>
          <p className="italic font-medium text-purple-800 border-l-4 border-purple-300 pl-4" data-id="element-122">
            "¿Por qué estoy tan cansada si incluso cuando descanso no paro de
            pensar?"
          </p>
          <p data-id="element-123">La respuesta no estaba en hacer más. Estaba en pensar menos.</p>
          <p data-id="element-124">
            No menos inteligencia. Menos ruido. Menos decisiones innecesarias.
            Menos carga invisible.
          </p>
          <p data-id="element-125">Y cuando eso se ordena… la energía vuelve sola.</p>
        </section>

        {/* Feelings */}
        <section className="mb-20 text-center" data-id="element-126">
          <h3 className="text-2xl font-bold text-[#4A3B69] mb-8" data-id="element-127">
            Lo que vas a sentir mientras lo leés
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-128">
            {['alivio', 'comprensión', 'validación', 'claridad', 'liviandad'].map((word, i) => <span key={i} className="bg-white px-6 py-3 rounded-full shadow-sm text-purple-700 font-medium border border-purple-100" data-id="element-129">
                {word}
              </span>)}
          </div>
          <p className="mt-8 text-gray-600 max-w-lg mx-auto" data-id="element-130">
            No porque cambies todo. Sino porque dejás de cargarte con lo que no
            te corresponde.
          </p>
        </section>

        {/* Bonuses Section */}
        <section className="mb-20" data-id="element-131">
          <h2 className="text-3xl font-bold text-center text-[#4A3B69] mb-2" data-id="element-132">
            Bonos de Regalo
          </h2>
          <p className="text-center text-red-500 font-bold mb-10 uppercase tracking-wider text-sm" data-id="element-133">
            Gratis solo por hoy
          </p>

          <div className="space-y-12" data-id="element-134">
            {/* Bonus 1 */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-purple-50 transform hover:-translate-y-1 transition-transform duration-300" data-id="element-135">
              <div className="flex flex-col md:flex-row gap-6 items-center" data-id="element-136">
                <div className="w-full md:w-1/3" data-id="element-137">
                  <img src="https://cdn.magicpatterns.com/uploads/7RaNY93aaLXHARc5wJuWHB/bonus_.png" alt="Checklist Decidir lo Mínimo" className="w-full rounded-lg shadow-md" data-id="element-138" />
                </div>
                <div className="w-full md:w-2/3" data-id="element-139">
                  <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-140">
                    Bono #1
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-141">
                    Checklist "Decidir lo Mínimo"
                  </h3>
                  <p className="text-gray-600" data-id="element-142">
                    Una herramienta simple para usar incluso en días difíciles.
                    Menos decisiones, más alivio mental.
                  </p>
                </div>
              </div>
            </div>

            {/* Bonus 2 */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-purple-50 transform hover:-translate-y-1 transition-transform duration-300" data-id="element-143">
              <div className="flex flex-col md:flex-row gap-6 items-center" data-id="element-144">
                <div className="w-full md:w-1/3" data-id="element-145">
                  <img src="https://cdn.magicpatterns.com/uploads/4z7RoCWT8k9zNyCuJGLDpd/bonuss.png" alt="Mini-Guía Qué hacer cuando estás saturada" className="w-full rounded-lg shadow-md" data-id="element-146" />
                </div>
                <div className="w-full md:w-2/3" data-id="element-147">
                  <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-148">
                    Bono #2
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-149">
                    Mini-Guía "Qué hacer cuando estás saturada"
                  </h3>
                  <p className="text-gray-600" data-id="element-150">
                    Tu guía de emergencia cuando estás mentalmente colapsada.
                  </p>
                </div>
              </div>
            </div>

            {/* Bonus 3 */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-purple-50 transform hover:-translate-y-1 transition-transform duration-300" data-id="element-151">
              <div className="flex flex-col md:flex-row gap-6 items-center" data-id="element-152">
                <div className="w-full md:w-1/3" data-id="element-153">
                  <img src="https://cdn.magicpatterns.com/uploads/mQ9fcnUrxrmznkfbsm2oYF/bonusss.png" alt="Rutina Mental de 5 Minutos" className="w-full rounded-lg shadow-md" data-id="element-154" />
                </div>
                <div className="w-full md:w-2/3" data-id="element-155">
                  <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-156">
                    Bono #3
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-157">
                    Rutina Mental de 5 Minutos
                  </h3>
                  <p className="text-gray-600" data-id="element-158">
                    Una rutina ultra mínima para cerrar el día sin ruidos
                    mentales.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="mb-20 relative" data-id="element-159">
          <div className="absolute inset-0 bg-purple-600 blur-[100px] opacity-10 rounded-full" data-id="element-160"></div>
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-purple-100 relative z-10" data-id="element-161">
            <div className="bg-[#4A3B69] p-4 text-center" data-id="element-162">
              <p className="text-white font-bold tracking-widest text-sm uppercase" data-id="element-163">
                Oferta Especial Limitada
              </p>
            </div>
            <div className="p-8 md:p-12 text-center" data-id="element-164">
              <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-165">
                Pack Completo: Ebook + 3 Bonos
              </h3>
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-166">
                <span className="text-gray-400 line-through text-2xl" data-id="element-167">
                  $34.990
                </span>
                <span className="text-5xl font-extrabold text-[#4A3B69]" data-id="element-168">
                  $19.990
                </span>
              </div>

              <div className="space-y-4 mb-8 text-left max-w-xs mx-auto" data-id="element-169">
                <div className="flex items-center gap-2" data-id="element-170">
                  <Check className="w-5 h-5 text-green-500" data-id="element-171" />
                  <span className="text-gray-700" data-id="element-172">
                    Ebook "No estás cansada..."
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-173">
                  <Check className="w-5 h-5 text-green-500" data-id="element-174" />
                  <span className="text-gray-700" data-id="element-175">
                    Checklist "Decidir lo Mínimo"
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-176">
                  <Check className="w-5 h-5 text-green-500" data-id="element-177" />
                  <span className="text-gray-700" data-id="element-178">Guía de Emergencia</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-179">
                  <Check className="w-5 h-5 text-green-500" data-id="element-180" />
                  <span className="text-gray-700" data-id="element-181">
                    Rutina Mental de 5 Minutos
                  </span>
                </div>
              </div>

              <CTAButton fullWidth className="mb-4" data-id="element-182">
                DESCARGAR AHORA
              </CTAButton>

              <p className="text-xs text-gray-500 flex items-center justify-center gap-1" data-id="element-183">
                <ShieldCheck className="w-3 h-3" data-id="element-184" />
                Pago 100% seguro y encriptado
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-20" data-id="element-185">
          <h2 className="text-3xl font-bold text-[#4A3B69] mb-10 text-center" data-id="element-186">
            Lo que dicen quienes ya lo leyeron
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-187">
            {testimonials.map((t, i) => <TestimonialCard key={i} {...t} data-id="element-188" />)}
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center mb-12" data-id="element-189">
          <h2 className="text-3xl font-bold text-[#4A3B69] mb-6" data-id="element-190">
            Tu próximo paso
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto" data-id="element-191">
            Si querés dejar de sentirte saturada, si querés recuperar claridad
            mental y volver a sentirte liviana sin exigirte más:
            <br data-id="element-192" />
            <br data-id="element-193" />
            <span className="font-bold text-purple-800" data-id="element-194">
              Este ebook es tu próximo paso.
            </span>
          </p>

          <div className="flex flex-col items-center gap-4" data-id="element-195">
            <CTAButton data-id="element-196">EMPEZÁ HOY MISMO</CTAButton>
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-4" data-id="element-197">
              <span data-id="element-198">👉 Descargalo ahora</span>
              <span data-id="element-199">👉 Empezá hoy</span>
              <span data-id="element-200">👉 Sentí el alivio</span>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-[#4A3B69] text-purple-200 py-12 text-center text-sm" data-id="element-201">
        <p data-id="element-202">© 2024 No estás cansada. Todos los derechos reservados.</p>
        <p className="mt-2 opacity-60" data-id="element-203">
          Este producto no garantiza resultados específicos. Los resultados
          pueden variar.
        </p>
      </footer>
    </div>;
}